import { FireIcon, UserIcon } from '@heroicons/react/24/outline';
import React from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { Skeleton } from '@/components/ui/skeleton';
import {
  useGetUserByIdQuery,
  useUpdateUserExtraMutation,
} from '@/services/api/userService';
import type { UserExtra } from '@/services/types/user-types';
import { selectUser } from '@/state/selectors/root-selectors';

import { HighFiveButton } from './HighFiveButton';
import { ManagingParalegalFirmsPanel } from './ManagingParalegalFirmsPanel';
import { UserDetailCard } from './UserDetailCard';
import { UserEventsTab } from './UserEventsTab';

// Main Component
export const UserDetailPage: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const currentUser = useSelector(selectUser);

  const {
    data: user,
    isLoading,
    error,
  } = useGetUserByIdQuery(userId || '', {
    skip: !userId,
  });

  const [updateUserExtra] = useUpdateUserExtraMutation();

  const isCurrentUser = currentUser?.id === user?.id;
  const extra: UserExtra = user?.extra || {};

  const handleUpdateExtra = async (updates: Partial<UserExtra>) => {
    if (!user) return;
    try {
      await updateUserExtra({
        userId: user.id,
        extra: { ...extra, ...updates },
      }).unwrap();
      toast.success('Profile updated');
    } catch {
      toast.error('Failed to update profile');
    }
  };

  if (isLoading) {
    return (
      <div className="container max-w-6xl mx-auto py-12 px-4 space-y-8">
        <Skeleton className="h-64 w-full rounded-3xl" />
        <Skeleton className="h-96 w-full rounded-3xl" />
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center text-center">
        <UserIcon className="mb-4 h-12 w-12 text-gray-300" />
        <h2 className="text-xl font-semibold text-gray-900">User not found</h2>
      </div>
    );
  }

  const highFives = extra.high_fives || 0;

  return (
    <div className="min-h-screen bg-zinc-50/50 pb-20 font-sans text-zinc-900">
      <div className="container max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* --- Bento Grid Layout --- */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-12 mb-8 items-start">
          {/* Left Column (Profile, High Fives, Managed Firms) */}
          <div className="col-span-1 lg:col-span-8 flex flex-col gap-6">
            <UserDetailCard
              user={user}
              isCurrentUser={isCurrentUser}
              onUpdateLocation={(location) => handleUpdateExtra({ location })}
              onUpdateBirthday={(birthday) => handleUpdateExtra({ birthday })}
              onUpdateTheme={(card_theme) => handleUpdateExtra({ card_theme })}
              onUpdateStatus={(status) => handleUpdateExtra({ status })}
            />

            {/* High Fives */}
            <div className="flex justify-start">
              <HighFiveButton
                currentValue={highFives}
                onUpdate={(newValue) =>
                  handleUpdateExtra({ high_fives: newValue })
                }
              />
            </div>

            {/* Managed Firms Panel - Only shows if user manages firms */}
            <ManagingParalegalFirmsPanel userId={user.id} />
          </div>

          {/* Right Column (Recent Activity) */}
          <div className="col-span-1 lg:col-span-4 flex flex-col gap-4">
            <div className="rounded-3xl border border-zinc-200/50 bg-white p-6 shadow-[0_2px_8px_rgba(0,0,0,0.02)]">
              <div className="mb-6 flex items-center gap-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-orange-50">
                  <FireIcon className="h-4 w-4 text-orange-500" />
                </div>
                <h2 className="text-lg font-bold tracking-tight text-zinc-900">
                  Recent Activity
                </h2>
              </div>
              <UserEventsTab userId={user.id} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
